import type { AnswerPayload, IAnswer, IResponseState, ISnapshotUsage } from '../api/contracts';
import {
  addressKey,
  answerInstanceKey,
  indexAnswers,
  isChildTriggered,
  isUsageVisible,
  selectedCodes,
  type IDefinitionView,
  type ISectionView,
  type IUsageNode
} from './snapshot-view';
import { MEMBER_LOOKUPS } from './lookup-shapes';
// Type-only, so the cycle with `use-response` (which imports this module for its value
// exports) exists in the type graph and never in the bundle. The address triple is that
// module's own vocabulary — every caller here is handing addresses back to it.
import type { IAnswerAddress } from './use-response';

/**
 * What a prefilled answer says to the respondent.
 *
 * survey-api SR-206 made `prior_cycle` the first prefill source that populates anything:
 * a returning participant's new response opens carrying last time's answers, each an
 * ordinary answer with an `answer_provenance` row behind it. Its SEC ADV block made `sec_adv`
 * the second — an RIA response opens with the firm's Form ADV record proposed, in the same
 * vocabulary and through the same confirmations — and SR-213 `registration` the third: a
 * participant's first response of a survey opens with what its registration typed (a name,
 * a firm) proposed, now that the completion opens no response of its own. This module is the
 * single reader of that vocabulary — the state mapping, the per-source copy and the rendering
 * of `provenance.sourceValue` — so that the three cannot drift apart. Design:
 * `docs/superpowers/specs/2026-09-14-prior-cycle-prefill-ui.md`.
 *
 * It renders a value **for reading**, which is why it is not
 * `answer-values.ts`: that module is the write path's translation between a payload and
 * what a control holds, and a control holds option *codes*. A respondent shown
 * `PE_ROLLUP_SINGLE_BRAND` under their own question has been shown the database, so codes
 * are resolved through the usage's own option list here. It is not
 * `domains/admin/answer-text.ts` either, and for the mirror image of the same reason: that
 * one prints codes deliberately, because a reviewer comparing a proposal against a stored
 * row wants the stored spelling.
 */

/** The three states the form renders. `rejected` is not among them: survey-api deletes the
 *  answer and cascades the provenance row with it, so nothing survives to render. */
export type PrefillState = 'proposed' | 'confirmed' | 'overridden';

export interface IPrefillView {
  state: PrefillState;
  /** Names the source in front of a value: "Previously: Boston". */
  sourceLabel: string;
  /** Names it as a sentence the confirm strip completes: "Previously answered — please
   *  confirm". Two spellings rather than one, because a source that names a record reads
   *  as a sentence already and the carry-over does not. */
  proposedLabel: string;
  /** What the source proposed, rendered for reading; null when there is nothing to show. */
  priorText: Nullable<string>;
  /**
   * The question the confirm strip asks in place of naming the source — "Is this your team?" for
   * a team's name carried from a team's submitted roster (`provenance.field`, SR-416's
   * cross-survey source, PS §5) — and null for every other proposal. Its "Yes" and "No" are the
   * same two decisions as "Confirm" and "Not correct".
   */
  question: Nullable<string>;
  /**
   * The day the proposal was true of, for a source whose `asOf` is the date of the value — a
   * figures row's ("as of Mar 31, 2027") — and null for every other source: `prior_cycle`'s is a
   * lock time (`SOURCE_COPY`), and a registry's the day it was asked.
   */
  asOfText: Nullable<string>;
}

/** One source's two spellings of its own name, as `IPrefillView` carries them apart:
 *  `label` in front of a value, `proposed` as the sentence the confirm strip completes. */
interface ISourceCopy {
  label: string;
  proposed: string;
}

/**
 * How each source is named to a respondent.
 *
 * `prior_cycle` says **"Previously"** and deliberately names no year. Three facts rule one
 * out: `provenance.asOf` is the source response's `locked_at` rather than the cycle it
 * answered (a 2026 answer locked in 2027 would read "2027"); the cycle code the proposal
 * carries lives in `source_ref`, which survey-api does not put on the wire; and SR-206's D4
 * carries from an *unsubmitted* source on purpose — every migrated response is `locked`
 * with `submitted_at` null — so "your last submission" would be false for exactly the
 * participants this feature exists for. "Previously" is true under all three.
 *
 * The other sources are not carry-overs but records held *about* the respondent, so they
 * name the record instead. `sec_adv` populates since survey-api's SEC ADV block, and
 * `registration` since SR-213's Teams block — the respondent's own words from the sign-up,
 * named as such, since "On file" would hide that they typed it themselves. The rest want
 * provider integrations this product does not have yet, and their entries are what keeps the
 * copy honest on the day one does.
 */
const SOURCE_COPY: Record<string, ISourceCopy> = {
  prior_cycle: { label: 'Previously', proposed: 'Previously answered' },
  sec_adv: { label: 'From your Form ADV record', proposed: 'From your Form ADV record' },
  registration: { label: 'Registration', proposed: 'From your registration' },
  brokercheck: { label: 'From BrokerCheck', proposed: 'From BrokerCheck' },
  // SR-396's: no usage declares it yet, and the owner ruled the words for the day one does (Q1).
  iapd: { label: 'From your IAPD record', proposed: 'From your IAPD record' },
  nipr: { label: 'From your NIPR record', proposed: 'From your NIPR record' },
  sec_site: { label: 'From the SEC', proposed: 'From the SEC' },
  // SR-416's: a team's figures, proposed into a multi-year table's rows (TEAM-PM-001). survey-api's
  // own words for the source — `SubmittedContentBuilder` names it so on the submission PDF.
  figures: { label: 'From our records', proposed: 'From our records' },
  cross_survey: {
    label: 'From another survey you filled in',
    proposed: 'From another survey you filled in'
  }
};

/**
 * The fallback is deliberately vague rather than the source's own name. survey-api's
 * `PrefillSources` carries thirteen (`iapd` since its SR-396, `figures` since SR-416), and the
 * four not named above —
 * `same_response`, `ai_inference`, `mariadb`, `salesforce_export` — are internal provenance: a
 * respondent reading "salesforce_export — please confirm" under their own question has been
 * shown a pipeline. "On file" is true of all four and of anything added
 * later; naming the source is what the table above is for, once someone decides what a
 * respondent should be told it is.
 */
const UNNAMED_SOURCE: ISourceCopy = { label: 'On file', proposed: 'Already on file' };

/**
 * What the confirm strip asks of a cross-survey proposal, by its `provenance.field` — survey-api's
 * `PrefillFieldConventions` names two (PS §5). A field not named here is a proposal like any other.
 */
const CROSS_SURVEY_QUESTIONS: Record<string, string> = {
  'team.name': 'Is this your team?',
  'firm.name': 'Is this your firm?'
};

/** The registries that propose a person's facts — a member's names after a lookup, an advisor's
 *  office address — which the respondent may refuse as a whole ("Not this person", "Not my
 *  address"). */
export const PERSON_REGISTRIES: ReadonlySet<string> = new Set(['brokercheck', 'iapd']);

/** The sources whose `asOf` is the day the value was true of — see `IPrefillView.asOfText`. */
const DATED_SOURCES = new Set(['figures']);

export function sourceCopy(source: string): ISourceCopy {
  return SOURCE_COPY[source] ?? UNNAMED_SOURCE;
}

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
};

/** Three uppercase letters — the shape `Intl.NumberFormat` accepts as a currency. A
 *  payload carrying anything else would throw a RangeError mid-render. */
const CURRENCY_CODE = /^[A-Z]{3}$/;

/**
 * One payload, rendered for reading.
 *
 * `cells` answers null rather than a string: a row of cells belongs *in* the table next to
 * the row it proposes, not flattened into a sentence under it. A table row's proposal is said
 * on the row itself (`response-field.tsx`, `tr[data-prefill]`), its proposed values in its
 * own cells.
 */
export function priorValueText(
  payload: Nullable<AnswerPayload> | undefined,
  usage: ISnapshotUsage
): Nullable<string> {
  if (!payload) return null;

  switch (payload.t) {
    case 'text':
      return payload.v === '' ? null : payload.v;
    case 'date':
      return formatDate(payload.v);
    case 'int':
    case 'num':
      return formatNumber(payload.v);
    case 'pct':
      return `${formatNumber(payload.v)}%`;
    case 'money':
      return formatMoney(payload.v, payload.cur);
    case 'opts':
      if (payload.v.length === 0) return null;
      return payload.v.map((code) => optionLabel(usage, code)).join(', ');
    case 'cells':
      return null;
    default:
      // The union is closed today. A tag added server-side must not render as `[object
      // Object]` under a respondent's question — better to say nothing than to say that.
      return null;
  }
}

/**
 * The prefill state of one answer, or null when it carries no provenance at all — which is
 * every answer the respondent typed themselves.
 *
 * Decided by `provenance.state` and never by `answer.origin`: `Response.SetValue` does not
 * touch `Origin`, so an answer the respondent has rewritten by hand still reads
 * `origin: "prefill"` for the life of the response. `origin` says where the row came from;
 * this asks what has happened to it since.
 */
export function prefillView(
  answer: IAnswer | undefined,
  usage: ISnapshotUsage
): Nullable<IPrefillView> {
  const provenance = answer?.provenance;
  if (!provenance) return null;
  if (
    provenance.state !== 'proposed' &&
    provenance.state !== 'confirmed' &&
    provenance.state !== 'overridden'
  ) {
    return null;
  }

  const copy = sourceCopy(provenance.source);
  return {
    state: provenance.state,
    sourceLabel: copy.label,
    proposedLabel: copy.proposed,
    priorText: priorValueText(provenance.sourceValue, usage),
    question: provenance.field ? (CROSS_SURVEY_QUESTIONS[provenance.field] ?? null) : null,
    // The day alone: a figures row's `asOf` is midnight UTC of its date.
    asOfText: DATED_SOURCES.has(provenance.source) ? formatDate(provenance.asOf.slice(0, 10)) : null
  };
}

/**
 * What a table row's proposed cells held, for an `overridden` row that says what it was proposed
 * as: "team_aum 1,500,000", each proposed non-null cell by its column's label (its code until
 * survey-api sends one — block 4's Q16), in the column order.
 */
export function proposedCellsText(
  sourceValue: Nullable<AnswerPayload> | undefined,
  columns: { code: string; label?: Nullable<string> }[]
): Nullable<string> {
  if (sourceValue?.t !== 'cells') return null;
  const proposed = lockedCells(sourceValue.v);
  const parts = columns
    .filter((column) => proposed.has(column.code))
    .map((column) => {
      const cell = sourceValue.v[column.code];
      const text = typeof cell === 'number' ? formatNumber(cell) : String(cell);
      return `${column.label ?? column.code} ${text}`;
    });
  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * What a prefill lock holds against the respondent: `'all'` — the whole answer — `'cells'` —
 * the proposed non-null cells of a table row, the row's other cells staying the respondent's —
 * or null.
 *
 * **A mirror of survey-api's `PrefillPolicy.Locks` / `.Refuses`** (`Domain/Json/PrefillPolicy.cs`,
 * SR-416's B2-12 and B4-3), and moves with it: survey-api refuses a respondent's write over a
 * lock `prefill_locked` — and with it every other answer of the same flush, since
 * `writes-rejected` is all-or-nothing — and a reject of a locked proposal `locked_usage`. So the
 * lock is drawn and the refused decision never offered (the roadmap's D3, D4); a refusal would
 * be the page's fault. `locked` locks whatever was proposed, even nothing. `locked_if_filled`
 * locks **the proposal, never the stored value** — `provenance.sourceValue`, the first provenance
 * row's — while it holds something: text not blank, options not empty, a table row with a
 * non-null cell, any other payload. A blank or absent proposal locks nothing, and stays so.
 * Judged here only to draw; survey-api judges the write.
 */
export function prefillLock(
  usage: ISnapshotUsage,
  answer: IAnswer | undefined
): Nullable<'all' | 'cells'> {
  const editability = usage.prefill?.editability;
  if (editability === 'locked') return 'all';
  if (editability !== 'locked_if_filled') return null;

  const proposal = answer?.provenance?.sourceValue;
  if (!proposal) return null;
  switch (proposal.t) {
    case 'text':
      return proposal.v.trim() === '' ? null : 'all';
    case 'opts':
      return proposal.v.length > 0 ? 'all' : null;
    case 'cells':
      return lockedCells(proposal.v).size > 0 ? 'cells' : null;
    default:
      return 'all';
  }
}

/**
 * Whether a proposal is one the respondent can only confirm (the survey review's FA1, T1; the
 * plan's Q9 b): its write and its reject both refused, and no "No, not this person" to drop it —
 * the Teams lead's locked answer (`IAnswer.locked`), or one a prefill lock holds whole outside the
 * names a member row's lookup proposed. survey-api raises no `engine.unconfirmed_proposal` on it,
 * so the form offers no "Confirm" over it and counts it nowhere; it stays `proposed`.
 *
 * **A mirror of survey-api's `ResponseStateMapper.ConfirmOnly`** (B1-14) and moves with it. The
 * lookup a name came from is not on the wire: a person registry's proposal at a member row's
 * name is taken as that row's lookup, the one source that writes there.
 */
export function isConfirmOnly(
  usage: ISnapshotUsage,
  answer: IAnswer | undefined,
  answers: Map<string, IAnswer>
): boolean {
  if (!answer || answer.provenance?.state !== 'proposed') return false;
  if (answer.locked) return true;
  if (prefillLock(usage, answer) !== 'all') return false;
  const { instanceId } = answer;
  const unlinkable =
    instanceId !== null &&
    PERSON_REGISTRIES.has(answer.provenance.source) &&
    MEMBER_LOOKUPS.some(
      (shape) =>
        (shape.firstName === usage.code || shape.lastName === usage.code) &&
        answers.get(addressKey(shape.crd, instanceId, ''))?.locked !== true
    );
  return !unlinkable;
}

/**
 * The columns of a row a `locked_if_filled` proposal holds — its non-null cells, which
 * survey-api refuses a write to change or drop (`PrefillPolicy.Refuses`). Empty for anything
 * that is no row of cells.
 */
export function lockedCells(proposal: unknown): Set<string> {
  if (!proposal || typeof proposal !== 'object' || Array.isArray(proposal)) return new Set();
  return new Set(
    Object.entries(proposal as Record<string, unknown>)
      .filter(([, cell]) => cell !== null && cell !== undefined)
      .map(([column]) => column)
  );
}

/**
 * Every proposal still waiting on a decision, as addresses the confirmation batch can be
 * built from.
 *
 * Three filters beyond `state === 'proposed'`, and all three exist because a proposal is
 * written wherever the target usage declares the step — visibility is not consulted,
 * deliberately, since survey-api's `visibility_default: 'hidden'` usages exist to be filled
 * and not shown. Counting those would put a number on screen the respondent cannot drive to
 * zero:
 *
 * - `isUsageVisible`, per instance, is what actually keeps a `visibility_default: 'hidden'`
 *   usage out: the engine's `IsRenderable` starts from the declared default, so those
 *   arrive as `usageVisibility: false`. Passing the row's `instanceId` is not optional —
 *   the flat entry is an OR over a usage's rows, so a gated member would count in every row
 *   the moment any row opened it.
 * - `isExcluded` is the engine's own verdict, and covers a hidden ancestor, a shrunk repeat
 *   row and a path miss.
 * - the `per_option_sub` gate mirrors what the form actually renders (`isChildTriggered`,
 *   the one deliberate client-side compensation in this app): the engine reports such a
 *   sub visible-and-required whether or not its trigger option is picked. Walked to the
 *   root rather than checked once, because the renderer applies it at every level and
 *   `FA-SV-002/criterion/other` is a shipped two-deep chain — a grandchild under an
 *   untriggered parent would otherwise be counted and never shown.
 *
 * And a proposal the respondent can only confirm (`isConfirmOnly`) is no decision left to them.
 *
 * The renderer reads a parent's selection through the draft overlay and this reads the
 * server's answers, so a count can lag a click by one write. That is the right direction to
 * be wrong in: the batch it feeds is addressed at what the server holds.
 */
export function pendingConfirmations(
  state: IResponseState,
  view: IDefinitionView
): IAnswerAddress[] {
  const answers = indexAnswers(state.answers);
  const pending: IAnswerAddress[] = [];

  for (const answer of state.answers) {
    if (answer.provenance?.state !== 'proposed' || answer.isExcluded) continue;

    const usage = view.usagesByCode.get(answer.usage);
    if (!usage) continue;
    if (!isUsageVisible(usage.code, state.render, answer.instanceId)) continue;

    if (!isTriggeredToRoot(usage, answer.instanceId, view, answers)) continue;
    if (isConfirmOnly(usage, answer, answers)) continue;

    pending.push({
      usage: answer.usage,
      instanceId: answer.instanceId,
      instanceKey: answer.instanceKey
    });
  }

  return pending;
}

/**
 * Whether the form draws the control at an address now: its row active, its usage visible
 * there, and every option-triggered link to its root open — `pendingConfirmations`' test of
 * what the respondent can act on, asked of an address that may hold no answer.
 */
export function isAddressDrawn(
  address: IAnswerAddress,
  state: IResponseState,
  view: IDefinitionView
): boolean {
  const usage = view.usagesByCode.get(address.usage);
  if (!usage) return false;
  const { instanceId } = address;
  if (
    instanceId !== null &&
    !state.repeats.some((row) => row.instanceId === instanceId && row.status === 'active')
  ) {
    return false;
  }
  if (!isUsageVisible(usage.code, state.render, instanceId)) return false;
  return isTriggeredToRoot(usage, instanceId, view, indexAnswers(state.answers));
}

/**
 * Every usage code a section renders, its children and repeat members included — what the
 * per-step panel needs to say "12 on this step" rather than "12 somewhere in this survey".
 *
 * A repeat member carries no `section` of its own on the wire, so the tree is what answers
 * this; filtering `usages[]` by `section` would silently lose every member and understate
 * the count in exactly the sections that carry the most proposals.
 */
export function sectionUsageCodes(view: ISectionView): Set<string> {
  const codes = new Set<string>();

  const walk = (node: IUsageNode) => {
    codes.add(node.usage.code);
    for (const child of node.children) walk(child);
    for (const member of node.members) walk(member);
  };
  for (const root of view.roots) walk(root);

  return codes;
}

/**
 * Whether every option-triggered link between this usage and its root is actually open —
 * the renderer's own walk (`visibleChildren` recurses), rather than one check against the
 * immediate parent.
 *
 * A parent is read at **its own** answer address, never at `''`: a parent that is itself a
 * `per_option_sub` stores its selection at its trigger code, and reading a blank key there
 * finds nothing and reports every grandchild untriggered.
 */
function isTriggeredToRoot(
  usage: ISnapshotUsage,
  instanceId: Nullable<string>,
  view: IDefinitionView,
  answers: Map<string, IAnswer>
): boolean {
  let current = usage;

  while (current.parentUsage) {
    const parent = view.usagesByCode.get(current.parentUsage);
    if (!parent) return true; // An orphan renders at the top level — nothing gates it.
    const parentAnswer = answers.get(
      addressKey(parent.code, instanceId, answerInstanceKey(parent))
    );
    if (!isChildTriggered(current, selectedCodes(parentAnswer))) return false;
    current = parent;
  }

  return true;
}

function optionLabel(usage: ISnapshotUsage, code: string): string {
  return usage.optionList?.options.find((option) => option.code === code)?.label ?? code;
}

function formatDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString('en-US', DATE_FORMAT);
}

/** Grouped, never rounded: this is a value the respondent gave, shown back to them. */
function formatNumber(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 20 });
}

function formatMoney(value: number, currency: string): string {
  if (!CURRENCY_CODE.test(currency)) return `${formatNumber(value)} ${currency}`;
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2
  });
}
